import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import OrdersTable from "../../components/Orders/OrdersTable";
import {
  fetchOrdersPage,
  showCachedPage,
  setPrefetching,
} from "../../redux/slices/orderSlice";

const PAGE_SIZE = 10;

function OrdersPage() {
  const dispatch = useDispatch();
  const {
    orders,
    total,
    totalPages,
    currentPage,
    pageCache,
    signature,
    loading,
    prefetching,
    error,
  } = useSelector((state) => state.orders);

  const [searchParams] = useSearchParams();
  const statusTab = (searchParams.get("status") || "all").toLowerCase();
  // The orders topbar tabs are: all / completed / paid / pending.
  // The backend's orderStatus enum is: pending / processing / completed / cancelled / refunded.
  // Map "paid" -> "processing" (that's the UI label that already lives in OrderStatusModal).
  const statusTabToOrderStatus = (tab) => {
    if (!tab || tab === "all") return "";
    if (tab === "paid") return "processing";
    return tab;
  };
  const [filterParams, setFilterParams] = useState({
    searchTerm: "",
    byCustomerName: "",
    byOrderStatus: statusTabToOrderStatus(statusTab),
    byDeliveryStatus: "",
    startDate: "",
    endDate: "",
    sortBy: "",
  });

  // Keep the local order-status filter in sync with the topbar tab.
  useEffect(() => {
    setFilterParams((prev) => ({
      ...prev,
      byOrderStatus: statusTabToOrderStatus(statusTab),
    }));
  }, [statusTab]);

  // Translate UI filter state into the request body for orders/filter.
  const buildFilters = useCallback(() => {
    let sortBy = "";
    let sortOrder = "";
    switch (filterParams.sortBy) {
      case "date-asc":   sortBy = "createdAt";    sortOrder = "asc";  break;
      case "date-desc":  sortBy = "createdAt";    sortOrder = "desc"; break;
      case "price-asc":  sortBy = "invoiceValue"; sortOrder = "asc";  break;
      case "price-desc": sortBy = "invoiceValue"; sortOrder = "desc"; break;
      default: break;
    }
    return {
      limit: PAGE_SIZE,
      byKeyword: filterParams.searchTerm || filterParams.byCustomerName || "",
      byOrderStatus: filterParams.byOrderStatus || "",
      byDeliveryStatus: filterParams.byDeliveryStatus || "",
      startDate: filterParams.startDate || "",
      endDate: filterParams.endDate || "",
      sortBy,
      sortOrder,
    };
  }, [filterParams]);

  // 1. Foreground fetch page 1 whenever filters change (debounced).
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchOrdersPage({
        filters: buildFilters(),
        page: 1,
        foreground: true,
      }));
    }, 300);
    return () => clearTimeout(timer);
  }, [dispatch, buildFilters]);

  // 2. Background-prefetch every remaining page once page 1 has landed.
  //    Sequential to avoid hammering the API. Cancelled if filters change.
  const prefetchRunRef = useRef("");
  useEffect(() => {
    if (loading) return;
    if (!totalPages || totalPages <= 1) return;
    if (!signature) return;
    // Don't re-run a prefetch we've already kicked off for this filter set.
    if (prefetchRunRef.current === signature) return;
    prefetchRunRef.current = signature;

    let cancelled = false;
    (async () => {
      dispatch(setPrefetching(true));
      for (let page = 2; page <= totalPages; page++) {
        if (cancelled) break;
        // Skip pages we've already cached (e.g. user navigated there manually).
        if (pageCache[page]) continue;
        try {
          await dispatch(fetchOrdersPage({
            filters: buildFilters(),
            page,
            foreground: false,
          })).unwrap();
        } catch {
          // Ignore — page can be re-tried lazily if the user navigates there.
        }
      }
      if (!cancelled) dispatch(setPrefetching(false));
    })();

    return () => {
      cancelled = true;
      dispatch(setPrefetching(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, signature, totalPages, loading]);

  const handleFilter = (filterObj) => {
    setFilterParams((prev) => ({ ...prev, ...filterObj }));
    // Page resets to 1 because the foreground useEffect always fetches page 1
    // on filter change.
  };

  const handlePageChange = (page) => {
    if (pageCache[page]) {
      // Instant — page is already loaded in the background.
      dispatch(showCachedPage(page));
    } else {
      // Cache miss (prefetch hasn't reached this page yet): fetch it.
      dispatch(fetchOrdersPage({
        filters: buildFilters(),
        page,
        foreground: true,
      }));
    }
  };

  const handleUpdateOrder = () => {
    // After an edit, refetch the visible page to reflect server state.
    dispatch(fetchOrdersPage({
      filters: buildFilters(),
      page: currentPage || 1,
      foreground: true,
    }));
  };

  const cachedCount = Object.keys(pageCache || {}).length;

  return (
    <div className="p-4">
      {error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <>
          <OrdersTable
            orders={orders}
            loading={loading}
            onUpdateOrder={handleUpdateOrder}
            onFilter={handleFilter}
            currentPage={currentPage || 1}
            totalPages={totalPages}
            total={total}
            itemsPerPage={PAGE_SIZE}
            onPageChange={handlePageChange}
          />
          {prefetching && totalPages > 1 && (
            <p className="mt-2 text-xs text-dashboardText">
              Loading remaining pages in background… ({cachedCount}/{totalPages})
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default OrdersPage;
